import {
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Box,
  CardActions,
  Switch,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory } from 'react-router';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import PropTypes from 'prop-types';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import { getOperatorDetails, updateOperator } from 'src/redux/operatorSlice';
import { getStateName } from 'src/utils/getInitials';
import { CONFIRMATION_MSG } from 'src/utils/constants';
import ROUTE from 'src/utils/routeConstants';
import PasswordResetModal from 'src/common/PasswordResetModal';
import NewOperator from './NewOperator';

/* ---------- Smart Text Component ---------- */
function SmartText({ value }) {
  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      setShowTooltip(
        textRef.current.scrollWidth > textRef.current.clientWidth
      );
    }
  }, [value]);

  return (
    <Tooltip title={showTooltip ? value : ''} arrow>
      <Typography
        ref={textRef}
        variant="body1"
        fontWeight={600}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {value || '--'}
      </Typography>
    </Tooltip>
  );
}

/* ✅ PropTypes added to fix ESLint */
SmartText.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ])
};

function OperatorDetails() {
  const theme = useTheme();
  const { userId } = useParams();
  const history = useHistory();
  const { loading, operatorDetails } = useSelector((state) => state.operators);
  const dispatch = useDispatch();

  const [openModal, setOpenModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassResetModal, setShowPassResetModal] = useState(false);

  useEffect(() => {
    dispatch(getOperatorDetails(userId));
  }, [dispatch, userId]);

  const updateStatus = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);
    try {
      const values = {
        ...operatorDetails,
        is_active: !operatorDetails.is_active
      };
      await dispatch(updateOperator(values)).unwrap();
      dispatch(
        setSuccessInfo({
          successMsg: 'Operator status updated successfully!'
        })
      );
    } catch (error) {
      dispatch(setErrorInfo({ errorMsg: error?.error_message }));
    }
    setIsLoading(false);
  };

  if (loading || isLoading) return <Loader />;

  const details = [
    { label: 'Employee ID', value: operatorDetails.emp_id },
    {
      label: 'User Name',
      value: `${operatorDetails.first_name} ${operatorDetails.last_name}`
    },
    { label: 'Site Name', value: operatorDetails.site_name },
    { label: 'Email', value: operatorDetails.email },
    { label: 'Phone Number', value: operatorDetails.mobile_num },
    {
      label: 'Address',
      value: `${operatorDetails.city}, ${getStateName(
        operatorDetails.state
      )}`
    },
    { label: 'Bowser Assigned', value: operatorDetails.tank_id }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NewOperator
        open={openModal}
        initialValues={operatorDetails}
        handleClose={() => setOpenModal(false)}
      />

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.05)',
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 140px)',
        }}
      >
        <CardHeader
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper'
          }}
          title={(
            <Box>
              <Typography variant="h6" fontWeight={700}>
                OPERATOR DETAILS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage operator configuration
              </Typography>
            </Box>
          )}
          action={(
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => history.replace(ROUTE.operators)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main
                }
              }}
            >
              Back
            </Button>
          )}
        />

        <CardContent sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            {details.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.label}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.02)
                    }
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      mb: 0.5
                    }}
                  >
                    {item.label}
                  </Typography>

                  <SmartText value={item.value} />
                </Box>
              </Grid>
            ))}

            <Grid item xs={12} sm={6} md={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    STATUS
                  </Typography>

                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={
                      operatorDetails.is_active
                        ? 'success.main'
                        : 'error.main'
                    }
                  >
                    {operatorDetails.is_active ? 'Active' : 'Inactive'}
                  </Typography>
                </Box>

                <Switch
                  checked={operatorDetails.is_active}
                  onChange={() => setShowConfirmModal(true)}
                  color="success"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>

        <Divider />

        <CardActions sx={{
          p: 2.5, justifyContent: 'flex-end', gap: 2, position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
        }}
        >
          <Button
            startIcon={<EditIcon />}
            variant="contained"
            onClick={() => setOpenModal(true)}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Edit Operator
          </Button>

          <Button
            startIcon={<LockResetIcon />}
            variant="contained"
            centerRipple
            onClick={() => setShowPassResetModal(true)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Reset Password
          </Button>
        </CardActions>
      </Card>

      <PasswordResetModal
        open={showPassResetModal}
        userEmail={operatorDetails?.email}
        onClose={() => setShowPassResetModal(false)}
      />

      <ConfirmationDialog
        info={
          operatorDetails.is_active
            ? CONFIRMATION_MSG.disableOperator
            : CONFIRMATION_MSG.enableOperator
        }
        open={showConfirmModal}
        disable={operatorDetails.is_active}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={updateStatus}
      />
    </Container>
  );
}

export default OperatorDetails;
